import { useEffect, useRef, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import API from "../../../services/api";
import {
  PhoneOutlined,
  EnvironmentOutlined,
  EyeOutlined,
  UserOutlined,
  GlobalOutlined,
  LinkOutlined,
  LinkedinOutlined,
  InstagramOutlined,
  TwitterOutlined,
} from "@ant-design/icons";
import { FaWhatsapp } from "react-icons/fa";
import { Spin, Empty, message } from "antd";
import "./BusinessDetails.css";

const TABS = ["Details", "Description", "Location", "Social"];

function BusinessDetails() {
  const { slug } = useParams();
  const [business, setBusiness] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeImage, setActiveImage] = useState(0);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewIndex, setPreviewIndex] = useState(0);
  const [activeTab, setActiveTab] = useState("Details");
  const [imageErrors, setImageErrors] = useState({});
  const hasTrackedView = useRef(false);
  const stripRef = useRef(null);
  const [images, setImages] = useState([]);
  const [otherImages, setOtherImages] = useState([]);

  const handleNext = useCallback(() => {
    if (otherImages.length > 0) {
      setPreviewIndex((prev) => (prev + 1) % otherImages.length);
    }
  }, [otherImages.length]);

  const handlePrev = useCallback(() => {
    if (otherImages.length > 0) {
      setPreviewIndex((prev) => (prev === 0 ? otherImages.length - 1 : prev - 1));
    }
  }, [otherImages.length]);

  const handleTabChange = useCallback((tab) => {
    setActiveTab(tab);
  }, []);

  const handleImageError = useCallback((index, isMain = false) => {
    setImageErrors((prev) => ({ ...prev, [index]: true }));
    if (isMain && images.length > 1) {
      const nextAvailableIndex = images.findIndex((_, idx) => !imageErrors[idx] && idx !== index);
      if (nextAvailableIndex !== -1) {
        setActiveImage(nextAvailableIndex);
      }
    }
  }, [images, imageErrors]);

  const getVisitorId = () => {
    let visitorId = localStorage.getItem("visitorId");
    if (!visitorId) {
      visitorId = `guest_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
      localStorage.setItem("visitorId", visitorId);
    }
    return visitorId;
  };

  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const isAdmin = user?.role === "admin";

  const formatDate = (date) => {
    if (!date) return "N/A";
    const parsed = new Date(date);
    return Number.isNaN(parsed.getTime()) ? "N/A" : parsed.toLocaleDateString();
  };

  const normalizePhone = (value) => value?.replace(/\D/g, "") || "";

  const ensureUrl = (url) => {
    if (!url) return null;
    if (/^https?:\/\//i.test(url)) return url;
    return `https://${url}`;
  };

  const renderDetailRow = (label, value) =>
    value !== null && value !== undefined && value !== "" ? (
      <tr key={label}>
        <td className="biz-detail-label">{label}</td>
        <td className="biz-detail-value">{value}</td>
      </tr>
    ) : null;

  const renderImageWithFallback = (src, alt, className, index, isMain = false) => {
    if (imageErrors[index]) {
      return (
        <div className={`${className} biz-image-fallback`}>
          <span>Image not available</span>
        </div>
      );
    }
    return (
      <img
        src={src}
        alt={alt}
        className={className}
        onError={() => handleImageError(index, isMain)}
        loading="lazy"
      />
    );
  };

  useEffect(() => {
    const fetchBusiness = async () => {
      try {
        setLoading(true);
        let res;
        try {
          res = await API.get(`/businesses/slug/${slug}`);
        } catch (err) {
          try {
            res = await API.get(`/businesses/${slug}`);
          } catch (error) {
            console.log("Both slug and ID fetch failed:", error);
            setBusiness(null);
            message.error("Failed to load business details");
            return;
          }
        }
        setBusiness(res.data);

        if (res.data?._id && !hasTrackedView.current) {
          hasTrackedView.current = true;
          try {
            const visitorId = getVisitorId();
            const viewRes = await API.post(
              `/businesses/${res.data._id}/view`,
              {},
              { headers: { "x-visitor-id": visitorId } }
            );
            setBusiness((prev) =>
              prev
                ? {
                  ...prev,
                  views:
                    typeof viewRes.data.views === "number"
                      ? viewRes.data.views
                      : prev.views,
                }
                : prev
            );
          } catch (viewError) {
            console.log("View count error:", viewError);
          }
        }
      } catch (err) {
        console.log(err);
        setBusiness(null);
        message.error("Error loading business details");
      } finally {
        setLoading(false);
      }
    };

    hasTrackedView.current = false;
    setActiveImage(0);
    fetchBusiness();
  }, [slug]);

  useEffect(() => {
    if (business) {
      const logoOnly = business.logo ? [business.logo] : [];
      const otherImagesList = (business.images || []).filter((img) => img !== business.logo);

      if (otherImagesList.length > 0) {
        setImages([otherImagesList[0]]);
        setOtherImages(otherImagesList);
      } else {
        setImages(logoOnly);
        setOtherImages([]);
      }
      setImageErrors({});
      setActiveImage(0);
    }
  }, [business]);

  useEffect(() => {
    if (previewOpen && stripRef.current) {
      const activeThumb = stripRef.current.children[previewIndex];
      if (activeThumb) {
        activeThumb.scrollIntoView({ block: "nearest", inline: "center" });
      }
    }
  }, [previewIndex, previewOpen]);

  useEffect(() => {
    if (previewOpen) {
      document.body.classList.add("no-scroll");
    } else {
      document.body.classList.remove("no-scroll");
    }
    return () => {
      document.body.classList.remove("no-scroll");
    };
  }, [previewOpen]);

  if (loading) {
    return (
      <div className="biz-state">
        <Spin size="large" />
      </div>
    );
  }

  if (!business) {
    return (
      <div className="biz-state">
        <Empty description="Business not found" />
      </div>
    );
  }

  const phone = normalizePhone(business.contactDetails?.phone);
  const websiteUrl = ensureUrl(business.website);
  const companyUrl = ensureUrl(business.companyUrl);
  const linkedinUrl = ensureUrl(business.socialLinks?.linkedin);
  const instagramUrl = ensureUrl(business.socialLinks?.instagram);
  const twitterUrl = ensureUrl(business.socialLinks?.twitter);

  const fullLocation = [
    business.location?.address,
    business.location?.city,
    business.location?.state,
    business.location?.country,
  ]
    .filter(Boolean)
    .join(", ");

  const cacheKey = business.updatedAt ? `?v=${encodeURIComponent(business.updatedAt)}` : "";

  return (
    <div className="biz-root">
      {/* Gallery */}
      <section className="biz-gallery">
        <div
          className="biz-gallery__main"
          onClick={() => {
            setPreviewIndex(0);
            setPreviewOpen(true);
          }}
        >
          {otherImages.length > 0 ? (
            renderImageWithFallback(
              otherImages[activeImage],
              business.companyName,
              "biz-gallery__main-img",
              activeImage,
              true
            )
          ) : (
            images[0] ? (
              renderImageWithFallback(
                images[0],
                business.companyName,
                "biz-gallery__main-img",
                0,
                true
              )
            ) : (
              <div className="biz-image-fallback">
                <span>Image not available</span>
              </div>
            )
          )}
          <div className="biz-gallery__overlay" />
          <button
            className="biz-gallery__badge"
            onClick={(e) => {
              e.stopPropagation();
              setPreviewIndex(0);
              setPreviewOpen(true);
            }}
            aria-label="View gallery"
          >
            <svg
              width="15"
              height="15"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
            >
              <rect x="3" y="3" width="18" height="18" rx="2" />
              <circle cx="8.5" cy="8.5" r="1.5" />
              <polyline points="21 15 16 10 5 21" />
            </svg>
            {otherImages.length > 0 ? otherImages.length : 0} photo{otherImages.length !== 1 ? "s" : ""}
          </button>
        </div>

        {otherImages.length > 0 && (
          <div className="biz-gallery__strip">
            {otherImages.map((img, i) => (
              <button
                key={i}
                className={`biz-gallery__thumb${activeImage === i ? " biz-gallery__thumb--on" : ""
                  }`}
                onClick={() => setActiveImage(i)}
                aria-label={`View photo ${i + 1}`}
              >
                {renderImageWithFallback(
                  img,
                  `Thumbnail ${i + 1}`,
                  "biz-gallery__thumb-img",
                  i
                )}
                {i === 4 && otherImages.length > 5 && (
                  <span className="biz-gallery__more">
                    +{otherImages.length - 5}
                  </span>
                )}
              </button>
            ))}
          </div>
        )}
      </section>

      {/* Body */}
      <div className="biz-body">
        {/* Left Content */}
        <main className="biz-content">
          {/* Breadcrumb */}
          <nav className="biz-breadcrumb" aria-label="Breadcrumb">
            <span>Businesses</span>
            <span className="biz-bc-sep">/</span>
            <span>{business.location?.city || "Listings"}</span>
            <span className="biz-bc-sep">/</span>
            <span className="biz-bc-active">{business.companyName}</span>
          </nav>

          {/* Header */}
          <div className="biz-header">
            <div className="biz-chips">
              <span className="biz-chip biz-chip--type">
                {business.industry || "Business"}
              </span>
              <span
                className={`biz-chip biz-chip--status-${business.status === "active" ? "active" : "inactive"
                  }`}
              >
                {business.status}
              </span>
              {isAdmin && (
                <span
                  className={`biz-chip biz-chip--approval-${business.approvalStatus}`}
                >
                  {business.approvalStatus}
                </span>
              )}
              {business.isFeatured && (
                <span className="biz-chip biz-chip--featured">⭐ Featured</span>
              )}
              {business.type && (
                <span className="biz-chip biz-chip--type">{business.type}</span>
              )}
            </div>

            <h1 className="biz-title">{business.companyName}</h1>

            {business.tagline && (
              <p className="biz-tagline">{business.tagline}</p>
            )}

            {fullLocation && (
              <p className="biz-location">
                <EnvironmentOutlined />
                {fullLocation}
              </p>
            )}
          </div>

          {/* Stats Bar */}
          <div className="biz-stats">
            <div className="biz-stat">
              <span className="biz-stat__icon">
                <UserOutlined />
              </span>
              <span className="biz-stat__val">{business.companySize || "—"}</span>
              <span className="biz-stat__lbl">Company Size</span>
            </div>
            <div className="biz-stat__div" />

            <div className="biz-stat">
              <span className="biz-stat__icon">
                <GlobalOutlined />
              </span>
              <span className="biz-stat__val">{business.foundedYear || "—"}</span>
              <span className="biz-stat__lbl">Founded</span>
            </div>
            <div className="biz-stat__div" />

            <div className="biz-stat">
              <span className="biz-stat__icon">
                <EnvironmentOutlined />
              </span>
              <span className="biz-stat__val">{business.location?.city || "—"}</span>
              <span className="biz-stat__lbl">Location</span>
            </div>

            {isAdmin && (
              <>
                <div className="biz-stat__div" />
                <div className="biz-stat">
                  <span className="biz-stat__icon">
                    <EyeOutlined />
                  </span>
                  <span className="biz-stat__val">{business.views || 0}</span>
                  <span className="biz-stat__lbl">Views</span>
                </div>
              </>
            )}
          </div>

          {/* Tabs */}
          <div className="biz-tabs">
            <div className="biz-tabs__nav" role="tablist">
              {TABS.map((tab) => (
                <button
                  key={tab}
                  role="tab"
                  aria-selected={activeTab === tab}
                  className={`biz-tabs__btn${activeTab === tab ? " biz-tabs__btn--on" : ""
                    }`}
                  onClick={() => handleTabChange(tab)}
                >
                  {tab}
                </button>
              ))}
            </div>

            <div className="biz-tabs__panel">
              {activeTab === "Details" && (
                <div className="biz-detail-grid">
                  <div className="biz-detail-card">
                    <h3 className="biz-detail-card__head">Business Details</h3>
                    <table className="biz-detail-table">
                      <tbody>
                        {isAdmin && renderDetailRow("Business ID", business._id)}
                        {renderDetailRow("Company Name", business.companyName)}
                        {renderDetailRow("Industry", business.industry)}
                        {renderDetailRow("Company Size", business.companySize)}
                        {renderDetailRow("Type", business.type)}
                        {renderDetailRow("Founded Year", business.foundedYear)}
                        {renderDetailRow("Status", business.status)}
                        {isAdmin && renderDetailRow("Approval Status", business.approvalStatus)}
                        {isAdmin && renderDetailRow("Views", business.views || 0)}
                        {isAdmin && renderDetailRow("Slug", business.slug)}
                        {renderDetailRow("Tagline", business.tagline)}
                      </tbody>
                    </table>
                  </div>

                  <div className="biz-detail-card">
                    <h3 className="biz-detail-card__head">Contact & Additional</h3>
                    <table className="biz-detail-table">
                      <tbody>
                        {renderDetailRow("Contact Name", business.contactDetails?.contactName)}
                        {renderDetailRow("Phone", business.contactDetails?.phone)}
                        {renderDetailRow("Email", business.contactDetails?.email)}
                        {renderDetailRow("Website", business.website)}
                        {renderDetailRow("Company URL", business.companyUrl)}
                        {renderDetailRow("Featured", business.isFeatured ? "Yes" : "No")}
                        {isAdmin && renderDetailRow("Featured Until", formatDate(business.featuredUntil))}
                        {renderDetailRow("Posted On", formatDate(business.createdAt))}
                        {isAdmin && renderDetailRow("Updated On", formatDate(business.updatedAt))}
                        {isAdmin && renderDetailRow("Total Images", business.images?.length || 0)}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {activeTab === "Description" && (
                <div className="biz-desc-card">
                  <p className="biz-desc-text">
                    {business.description || "No description available."}
                  </p>
                </div>
              )}

              {activeTab === "Location" && (
                <div className="biz-location-wrap">
                  <div className="biz-detail-grid">
                    <div className="biz-detail-card">
                      <h3 className="biz-detail-card__head">Address</h3>
                      <table className="biz-detail-table">
                        <tbody>
                          {renderDetailRow("Address", business.location?.address)}
                          {renderDetailRow("Locality", business.location?.locality)}
                          {renderDetailRow("City", business.location?.city)}
                          {renderDetailRow("State", business.location?.state)}
                          {renderDetailRow("Country", business.location?.country)}
                          {renderDetailRow("Pincode", business.location?.pincode)}
                        </tbody>
                      </table>
                    </div>
                    <div className="biz-detail-card">
                      <h3 className="biz-detail-card__head">Contact Info</h3>
                      <table className="biz-detail-table">
                        <tbody>
                          {renderDetailRow("Owner", business.ownerId?.name)}
                          {isAdmin && renderDetailRow("Owner Email", business.ownerId?.email)}
                          {renderDetailRow("Contact Name", business.contactDetails?.contactName)}
                          {renderDetailRow("Phone", business.contactDetails?.phone)}
                          {renderDetailRow("Contact Email", business.contactDetails?.email)}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === "Social" && (
                <div className="biz-social-card">
                  <h3 className="biz-detail-card__head">Social Links</h3>
                  <div className="biz-social-list">
                    {websiteUrl && (
                      <div className="biz-social-item">
                        <GlobalOutlined className="biz-social-icon" />
                        <span className="biz-social-label">Website</span>
                        <a
                          href={websiteUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="biz-social-link"
                        >
                          {business.website}
                        </a>
                      </div>
                    )}

                    {companyUrl && (
                      <div className="biz-social-item">
                        <LinkOutlined className="biz-social-icon" />
                        <span className="biz-social-label">Company URL</span>
                        <a
                          href={companyUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="biz-social-link"
                        >
                          {business.companyUrl}
                        </a>
                      </div>
                    )}

                    {linkedinUrl && (
                      <div className="biz-social-item">
                        <LinkedinOutlined className="biz-social-icon" />
                        <span className="biz-social-label">LinkedIn</span>
                        <a
                          href={linkedinUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="biz-social-link"
                        >
                          LinkedIn Profile
                        </a>
                      </div>
                    )}

                    {instagramUrl && (
                      <div className="biz-social-item">
                        <InstagramOutlined className="biz-social-icon" />
                        <span className="biz-social-label">Instagram</span>
                        <a
                          href={instagramUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="biz-social-link"
                        >
                          Instagram Profile
                        </a>
                      </div>
                    )}

                    {twitterUrl && (
                      <div className="biz-social-item">
                        <TwitterOutlined className="biz-social-icon" />
                        <span className="biz-social-label">Twitter</span>
                        <a
                          href={twitterUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="biz-social-link"
                        >
                          Twitter Profile
                        </a>
                      </div>
                    )}

                    {!websiteUrl && !companyUrl && !linkedinUrl && !instagramUrl && !twitterUrl && (
                      <p className="biz-empty-msg">No social or website links available</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Rejection Notice */}
          {business.approvalStatus === "rejected" && business.rejectionReason && (
            <div className="biz-rejection">
              <strong>Rejection Reason</strong>
              <p>{business.rejectionReason}</p>
            </div>
          )}
        </main>

        {/* Sidebar */}
        <aside className="biz-sidebar">
          <div className="biz-sidebar__card">
            {business.logo && (
              <>
                <div className="biz-sidebar__logo">
                  <img
                    src={`${business.logo}${cacheKey}`}
                    alt={business.companyName}
                    className="biz-sidebar__logo-img"
                  />
                </div>
                <hr className="biz-sidebar__rule" />
              </>
            )}

            <div className="biz-sidebar__owner">
              <span className="biz-sidebar__avatar" aria-hidden="true">
                <UserOutlined />
              </span>
              <div className="biz-sidebar__owner-info">
                <span className="biz-sidebar__owner-name">
                  {business.contactDetails?.contactName || business.ownerId?.name || "Contact Owner"}
                </span>
                <span className="biz-sidebar__owner-role">Business Owner</span>
              </div>
            </div>

            {(business.contactDetails?.phone || business.contactDetails?.email) && (
              <dl className="biz-sidebar__contact-list">
                {business.contactDetails?.phone && (
                  <>
                    <dt>Phone</dt>
                    <dd>{business.contactDetails.phone}</dd>
                  </>
                )}
                {business.contactDetails?.email && (
                  <>
                    <dt>Email</dt>
                    <dd className="biz-sidebar__email">{business.contactDetails.email}</dd>
                  </>
                )}
              </dl>
            )}

            {phone && (
              <div className="biz-sidebar__actions">
                <a href={`tel:${phone}`} className="biz-btns biz-btn--call">
                  <PhoneOutlined style={{ transform: "rotate(90deg)" }} />
                  Call Now
                </a>
                <a
                  href={`https://wa.me/${phone}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="biz-btns biz-btn--wa"
                >
                  <FaWhatsapp />
                  WhatsApp
                </a>
              </div>
            )}

            <hr className="biz-sidebar__rule" />

            <dl className="biz-sidebar__facts">
              <div className="biz-sidebar__fact">
                <dt>Industry</dt>
                <dd>{business.industry || "—"}</dd>
              </div>
              <div className="biz-sidebar__fact">
                <dt>Company Size</dt>
                <dd>{business.companySize || "—"}</dd>
              </div>
              <div className="biz-sidebar__fact">
                <dt>Founded</dt>
                <dd>{business.foundedYear || "—"}</dd>
              </div>
              <div className="biz-sidebar__fact">
                <dt>Location</dt>
                <dd>{business.location?.city || "—"}</dd>
              </div>
              <div className="biz-sidebar__fact">
                <dt>Posted</dt>
                <dd>
                  {business.createdAt
                    ? new Date(business.createdAt).toLocaleDateString()
                    : "—"}
                </dd>
              </div>
            </dl>

            {isAdmin && (
              <div className="biz-sidebar__admin">
                <span className="biz-sidebar__admin-label">
                  Listed by (Admin)
                </span>
                <span>{business.ownerId?.name || "N/A"}</span>
                <span>{business.ownerId?.email || "N/A"}</span>
              </div>
            )}
          </div>

          <p className="biz-sidebar__note">
            Listed on{" "}
            {business.createdAt
              ? new Date(business.createdAt).toLocaleDateString("en-IN", {
                day: "numeric",
                month: "long",
                year: "numeric",
              })
              : "—"}
          </p>
        </aside>
      </div>

      {/* Mobile Sticky Footer */}
      {phone && (
        <div
          className="biz-mobile-bar"
          role="complementary"
          aria-label="Contact actions"
        >
          <div className="biz-mobile-bar__contact">
            <span className="biz-mobile-bar__name">
              {business.companyName}
            </span>
          </div>
          <div className="biz-mobile-bar__btns">
            <a href={`tel:${phone}`} className="biz-mbtn biz-mbtn--call">
              <PhoneOutlined style={{ transform: "rotate(90deg)" }} /> Call
            </a>
            <a
              href={`https://wa.me/${phone}`}
              target="_blank"
              rel="noopener noreferrer"
              className="biz-mbtn biz-mbtn--wa"
            >
              <FaWhatsapp /> WhatsApp
            </a>
          </div>
        </div>
      )}

      {/* Lightbox */}
      {previewOpen && (
        <div
          className="biz-lb"
          role="dialog"
          aria-modal="true"
          aria-label="Image preview"
          onClick={() => setPreviewOpen(false)}
        >
          <div
            className="biz-lb__blur"
            style={{ backgroundImage: `url(${otherImages[previewIndex] || images[0]})` }}
          />

          <button
            className="biz-lb__close"
            onClick={() => setPreviewOpen(false)}
            aria-label="Close preview"
          >
            ✕
          </button>

          <span className="biz-lb__counter">
            {previewIndex + 1} / {otherImages.length}
          </span>

          <div className="biz-lb__stage" onClick={(e) => e.stopPropagation()}>
            <button
              className="biz-lb__nav biz-lb__nav--prev"
              onClick={handlePrev}
              aria-label="Previous image"
            >
              ‹
            </button>
            {renderImageWithFallback(
              otherImages[previewIndex],
              `Preview ${previewIndex + 1}`,
              "biz-lb__img",
              previewIndex
            )}
            <button
              className="biz-lb__nav biz-lb__nav--next"
              onClick={handleNext}
              aria-label="Next image"
            >
              ›
            </button>
          </div>

          {otherImages.length > 1 && (
            <div
              className="biz-lb__strip"
              ref={stripRef}
              onClick={(e) => e.stopPropagation()}
            >
              {otherImages.map((img, i) => (
                <button
                  key={i}
                  className={`biz-lb__thumb${previewIndex === i ? " biz-lb__thumb--on" : ""
                    }`}
                  onClick={() => setPreviewIndex(i)}
                  aria-label={`Go to photo ${i + 1}`}
                >
                  {renderImageWithFallback(img, `Thumbnail ${i + 1}`, "", i)}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default BusinessDetails;